import React, { useState, useEffect } from 'react'
import { ColumnDefinition } from 'common/components/DataTable'
import GenericList from 'common/components/GenericList'
import { Button, Form } from 'react-bootstrap'
import * as Icon from 'react-bootstrap-icons'
import { useNavigate } from 'react-router-dom'
import API from 'common/logic/API'
import OperationWorkflowModal from 'common/components/OperationWorkflowModal'
import EditAssetWorkflow from './EditAssetWorkflow'
import { FinancialAsset, FinancialAssetsTotal, Fund } from 'types'
import Utils from 'common/Utils'

function AssetPortfolio() {
    const navigate = useNavigate()
    const [modal, setModal] = useState<{ type: string; id?: number; name?: string } | null>(null)
    const [dataKey, setDataKey] = useState(Date.now())
    const [investmentFunds, setInvestmentFunds] = useState<Fund[]>([])
    const [fundFilter, setFundFilter] = useState('')
    const [nameSearch, setNameSearch] = useState('')
    const [appliedNameSearch, setAppliedNameSearch] = useState('')
    const [hideZero, setHideZero] = useState(true)
    const [totalValue, setTotalValue] = useState<string | null>(null)

    useEffect(() => {
        API.get('/funds').then((r: any) => {
            const funds = (r.data?.results ?? []) as Fund[]
            setInvestmentFunds(funds.filter(f => f.isInvestment && !f.deleted))
        }).catch(() => {})
    }, [])

    const hideDialogs = () => setModal(null)
    const refreshData = () => setDataKey(Date.now())

    const endpointParams: Record<string, unknown> = {}
    if (fundFilter) endpointParams.fund = fundFilter
    if (appliedNameSearch) endpointParams.name = appliedNameSearch
    if (hideZero) endpointParams.hideZero = 'true'

    useEffect(() => {
        API.get('/financial-assets/total')
            .then((r: any) => {
                const total = r.data as FinancialAssetsTotal
                setTotalValue(total?.value ?? '0')
            })
            .catch(() => setTotalValue(null))
    }, [dataKey])

    const applyNameSearch = () => setAppliedNameSearch(nameSearch)

    const columns = [
        new ColumnDefinition<FinancialAsset>('Name', obj => <a href={`/investments/assets/${obj.id}`}>{obj.name}</a>),
        new ColumnDefinition<FinancialAsset>('Fund', obj => obj.fund.name, 'td-size-s'),
        new ColumnDefinition<FinancialAsset>('Amount', obj => <>{Utils.formatPreciseAmount(obj.amountHeld)}</>, 'td-currency', 'td-right'),
        new ColumnDefinition<FinancialAsset>('Average cost', obj =>
            Number(obj.amountHeld) > 0 ? <>{Utils.formatPreciseAmount(obj.averageCost)} €</> : <span className="text-muted">—</span>,
            'td-currency', 'td-right'
        ),
        new ColumnDefinition<FinancialAsset>('Value', obj => <>{Utils.formatMoney(obj.value)} €</>, 'td-currency', 'td-right'),
        new ColumnDefinition<FinancialAsset>('Notes', obj => <small className="text-muted">{obj.notes}</small>),
        new ColumnDefinition<FinancialAsset>('Actions', obj => <>
            <Button variant="link" title="Edit" onClick={() => setModal({ type: 'edit', id: obj.id })}><Icon.Pencil /></Button> |
            <Button variant="link" title="Delete" onClick={() => setModal({ type: 'delete', id: obj.id, name: obj.name })}><Icon.Trash /></Button>
        </>)
    ]

    let modalEl: React.ReactNode = null
    if (modal) {
        const completion = (result: unknown) => { hideDialogs(); if (result) refreshData() }
        switch (modal.type) {
            case 'create':
            case 'edit':
                modalEl = <EditAssetWorkflow onSuccess={completion} entryId={modal.id} />
                break
            case 'delete':
                modalEl = <OperationWorkflowModal
                    confirmationStep={() => `Delete asset "${modal.name}"?`}
                    executionStep={() => API.delete(`/financial-assets/${modal.id}`)}
                    completionCallback={completion}
                />
                break
        }
    }

    const filterBar = (
        <div className="mb-2 d-flex align-items-center" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
            <Form.Control
                as="select"
                size="sm"
                value={fundFilter}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => { setFundFilter(e.target.value); setDataKey(Date.now()) }}
                style={{ width: '160px' }}
            >
                <option value="">All investment funds</option>
                {investmentFunds.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
            </Form.Control>
            <Form.Control
                size="sm"
                type="text"
                placeholder="Search by name…"
                value={nameSearch}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNameSearch(e.target.value)}
                onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter') { applyNameSearch(); setDataKey(Date.now()) } }}
                style={{ width: '200px' }}
            />
            <Button size="sm" variant="outline-secondary" onClick={() => { applyNameSearch(); setDataKey(Date.now()) }}>
                <Icon.Search />
            </Button>
            <Form.Check
                type="checkbox"
                id="hide-zero-amounts"
                label="Hide zero amounts"
                checked={hideZero}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setHideZero(e.target.checked); setDataKey(Date.now()) }}
            />
            {(fundFilter || appliedNameSearch || !hideZero) && (
                <Button size="sm" variant="link" onClick={() => {
                    setFundFilter('')
                    setNameSearch('')
                    setAppliedNameSearch('')
                    setHideZero(true)
                    setDataKey(Date.now())
                }}>Clear filters</Button>
            )}
        </div>
    )

    return (
        <>
            {modalEl}
            <GenericList
                title="Investments Portfolio"
                columns={columns as unknown as ColumnDefinition[]}
                endpoint="/financial-assets"
                endpointParams={endpointParams}
                beforeTable={<>
                <div>
                    {totalValue !== null && <span className="ml-2"><strong>Current unrealized value:</strong> {Utils.formatMoney(totalValue)} €</span>}
                </div>
                <div>
                    <Button variant="link" onClick={() => setModal({ type: 'create' })}>Add new</Button>
                    {' | '}
                    <Button variant="link" onClick={() => navigate('/investments/assets/import')}>Import</Button>
                </div>
                {filterBar}
                </>}
                dataKey={dataKey}
            />
        </>
    )
}

export default AssetPortfolio
